import { useTranslation } from 'react-i18next';
import { Button, Modal, Select } from '@/components/ui';
import { useOrganizationOptions, useOrgLevelOptions } from '@/pages/Console/Accounts/Shared/useOrgLevelPicker';
import type { RowTransferVM } from './useRowTransfer';

/**
 * EAA-1820 (frame 85, "Chuyển đơn vị"): org picker, then — ONLY when the row
 * being moved is level-based — that destination org's Level picker. See
 * `useRowTransfer`'s docstring for exactly why the level field must not render
 * at all for a direct-grant row (`target_level_id` must be OMITTED there, or
 * the server 400s).
 */
export function RowTransferDialog({ vm }: { vm: RowTransferVM }) {
    const { t } = useTranslation();
    const open = Boolean(vm.target);
    const orgs = useOrganizationOptions(open);
    const levels = useOrgLevelOptions(vm.targetOrgId, open && vm.needsLevel);
    const identity = vm.target?.user_email || vm.target?.user_sub || '';

    return (
        <Modal
            open={open}
            onCancel={vm.close}
            title={t('console.hierarchy.scopedAccounts.rowTransfer.title')}
            // EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`
            // — see AccountDetailModal.tsx for the mapping rationale.
            footer={
                <>
                    <Button onClick={vm.close} disabled={vm.busy}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="primary" onClick={vm.submit} disabled={vm.busy || !vm.canSubmit}>
                        {vm.busy
                            ? t('console.hierarchy.scopedAccounts.rowTransfer.submitBusy')
                            : t('console.hierarchy.scopedAccounts.rowTransfer.submitButton')}
                    </Button>
                </>
            }
        >
            <p className="portal-admin-hint">
                {t('console.hierarchy.scopedAccounts.rowTransfer.hint', { identity })}
            </p>
            <div className="portal-admin-form">
                <label className="sa-dialog-field">
                    {t('console.hierarchy.scopedAccounts.rowTransfer.orgLabel')}
                    <Select
                        value={vm.targetOrgId || undefined}
                        onChange={(next) => vm.setTargetOrgId((next as string) ?? '')}
                        options={orgs.options}
                        loading={orgs.loading}
                        placeholder={t('console.hierarchy.scopedAccounts.rowTransfer.orgPlaceholder')}
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                    />
                </label>
                {vm.needsLevel && (
                    <label className="sa-dialog-field">
                        {t('console.hierarchy.scopedAccounts.rowTransfer.levelLabel')}
                        <Select
                            value={vm.targetLevelId || undefined}
                            onChange={(next) => vm.setTargetLevelId((next as string) ?? '')}
                            options={levels.options}
                            loading={levels.loading}
                            disabled={!vm.targetOrgId}
                            placeholder={t('console.hierarchy.scopedAccounts.rowTransfer.levelPlaceholder')}
                            showSearch
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                        />
                    </label>
                )}
                {vm.error && <p className="portal-admin-error">{vm.error}</p>}
            </div>
        </Modal>
    );
}

export default RowTransferDialog;
